/**
 * Login Page
 *
 * Shown when user is not authenticated.
 * - In TWA (Mini App): automatically authenticates via initData (this page shouldn't show)
 * - In browser/PWA: Shows login options:
 *   1. Telegram Login Widget (primary)
 *   2. Email login (Magic Link or Password)
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth, type TelegramWidgetData } from "../../lib/auth.tsx";
import { config } from "../../config.ts";
import { isTelegramMiniApp } from "../../lib/telegram.ts";
import { IconTelegram } from "../../components/Icons.tsx";
import { TelegramLoginButton } from "../../components/TelegramLoginButton.tsx";
import { GraphQLClientError, getErrorMessage } from "../../lib/graphql.ts";
import {
  saveTokens,
  getRememberedEmail,
  setRememberedEmail,
  getLastEmails,
} from "../../lib/storage.ts";
import {
  useRequestMagicLink,
  useExchangeMagicCode,
  useLoginWithPassword,
} from "@/features/auth/hooks";
import { safeJsonParse } from "../../lib/json-utils.ts";

type LoginTab = "telegram" | "email";
type EmailStep = "password" | "code" | "email_not_found";

// iOS PWA: WebView is killed when app goes to background, causing full reload.
// We persist form state to sessionStorage to restore it after reload.
const LOGIN_STATE_KEY = "login_form_state";
const LOGIN_STATE_TTL = 10 * 60 * 1000; // 10 minutes

interface SavedLoginState {
  email: string;
  emailStep: EmailStep | "email"; // "email" for backward compat, mapped to "password"
  activeTab: LoginTab;
  timestamp: number;
}

function getSavedLoginState(): { email: string; emailStep: EmailStep; activeTab: LoginTab } | null {
  const saved = sessionStorage.getItem(LOGIN_STATE_KEY);
  if (!saved) return null;
  
  const state = safeJsonParse<SavedLoginState | null>(saved, null);
  if (!state || typeof state !== "object" || !state.timestamp) return null;
  
  if (Date.now() - state.timestamp > LOGIN_STATE_TTL) {
    sessionStorage.removeItem(LOGIN_STATE_KEY);
    return null;
  }
  const step: EmailStep = state.emailStep === "email" ? "password" : state.emailStep;
  return { ...state, emailStep: step };
}

function saveLoginState(state: Omit<SavedLoginState, "timestamp">): void {
  try {
    sessionStorage.setItem(LOGIN_STATE_KEY, JSON.stringify({
      ...state,
      timestamp: Date.now(),
    }));
  } catch {
    // Ignore storage errors
  }
}

function clearLoginState(): void {
  try {
    sessionStorage.removeItem(LOGIN_STATE_KEY);
  } catch {
    // Ignore
  }
}

export function LoginPage() {
  const { isLoading, error, isMiniApp, loginWithWidget } = useAuth();
  const [loginMethod, setLoginMethod] = useState<"twa" | "browser" | null>(null);
  
  // URQL hooks
  const { requestMagicLink, isLoading: isRequestingMagicLink } = useRequestMagicLink();
  const { exchangeMagicCode, isLoading: isExchangingCode } = useExchangeMagicCode();
  const { loginWithPassword, isLoading: isLoggingIn } = useLoginWithPassword();
  
  // Restore state from sessionStorage on mount (iOS PWA reload recovery)
  const savedState = useRef(getSavedLoginState());
  const [activeTab, setActiveTab] = useState<LoginTab>(savedState.current?.activeTab ?? "telegram");
  
  // Telegram Widget state
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const [showWidgetNotInBot, setShowWidgetNotInBot] = useState(false);
  
  // Email login state - restore from saved state or remembered email
  const [email, setEmail] = useState(
    savedState.current?.email ?? getRememberedEmail() ?? "",
  );
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState("");
  const [emailStep, setEmailStep] = useState<EmailStep>(savedState.current?.emailStep ?? "password");
  const [rememberEmail, setRememberEmail] = useState(true);
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Consolidated loading state
  const isEmailLoading = isRequestingMagicLink || isExchangingCode || isLoggingIn;

  // Persist form state to sessionStorage (for iOS PWA reload recovery)
  useEffect(() => {
    if (email) {
      saveLoginState({ email, emailStep, activeTab });
    }
  }, [email, emailStep, activeTab]);
  
  // Determine login method on mount
  useEffect(() => {
    if (isTelegramMiniApp()) {
      setLoginMethod("twa");
    } else {
      setLoginMethod("browser");
    }
  }, []);

  // Handle Telegram Widget callback
  const handleTelegramAuth = useCallback(
    async (user: TelegramWidgetData) => {
      setIsWidgetLoading(true);
      setWidgetError(null);
      setShowWidgetNotInBot(false);
      try {
        await loginWithWidget(user);
        // Success - auth context will redirect
      } catch (err) {
        if (err instanceof GraphQLClientError && err.code === "USER_NOT_IN_BOT") {
          setShowWidgetNotInBot(true);
        } else {
          setWidgetError(getErrorMessage(err, "Ошибка авторизации"));
        }
      } finally {
        setIsWidgetLoading(false);
      }
    },
    [loginWithWidget]
  );

  // Request magic link (for "Войти по коду" flow)
  const handleRequestMagicLink = async () => {
    if (!email?.trim()) {
      setEmailError("Введите email");
      return;
    }
    setEmailError(null);

    try {
      const result = await requestMagicLink(email.trim());
      const { sent, reason } = result;
      if (reason === "EMAIL_NOT_FOUND") {
        setEmailStep("email_not_found");
      } else if (reason === "RATE_LIMITED") {
        setEmailError("Слишком много запросов. Попробуйте через час.");
      } else if (sent) {
        setCode("");
        setEmailStep("code");
      } else {
        setEmailError("Ошибка отправки кода");
      }
    } catch (err) {
      setEmailError(getErrorMessage(err, "Ошибка отправки"));
    }
  };

  // Handle code submission
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    try {
      const result = await exchangeMagicCode(email, code);
      
      clearLoginState();
      await saveTokens(result.tokens);
      if (rememberEmail) setRememberedEmail(email);
      window.location.reload();
    } catch (err) {
      setEmailError(getErrorMessage(err, "Неверный код"));
    }
  };

  // Handle password login
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    try {
      const result = await loginWithPassword(email, password);
      
      clearLoginState();
      await saveTokens(result.tokens);
      if (rememberEmail) setRememberedEmail(email);
      window.location.reload();
    } catch (err) {
      setEmailError(getErrorMessage(err, "Неверный email или пароль"));
    }
  };

  // Loading state
  if (isLoading || loginMethod === null) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 page-container relative z-20 safe-area-top safe-area-bottom">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 xs:w-12 xs:h-12 border-3 border-slate-200 border-t-[var(--color-accent)] rounded-full animate-spin" />
          <p className="text-sm xs:text-base text-slate-500">Загрузка...</p>
        </div>
      </div>
    );
  }

  // If in TWA, this page shouldn't show (auth is automatic)
  if (loginMethod === "twa" || isMiniApp) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 page-container relative z-20 safe-area-top safe-area-bottom">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 xs:w-12 xs:h-12 border-3 border-slate-200 border-t-[var(--color-accent)] rounded-full animate-spin" />
          <p className="text-sm xs:text-base text-slate-500">Авторизация...</p>
        </div>
      </div>
    );
  }

  // Browser/PWA mode - show login options
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-3 xs:p-4 md-mobile:p-5 page-container relative z-20 safe-area-top safe-area-bottom">
      {/* Header — как на экране выбора роли */}
      <div className="text-center mb-5 xs:mb-6 md-mobile:mb-8">
        <img
          src="/logo.png"
          alt=""
          className="w-14 h-14 xs:w-16 xs:h-16 md-mobile:w-20 md-mobile:h-20 mx-auto mb-3 xs:mb-4 rounded-xl object-contain"
        />
        <h1 className="text-lg xs:text-xl md-mobile:text-2xl font-bold text-slate-900 mb-1">
          OneChurch
        </h1>
        <p className="text-sm xs:text-base text-slate-500">
          Евангельская церковь на Пхукете
        </p>
      </div>

      {/* Login Card — в одном стиле с экраном выбора роли */}
      <div className="card p-4 xs:p-5 md-mobile:p-6 w-full max-w-sm">
        <h2 className="text-base xs:text-lg md-mobile:text-xl font-semibold text-slate-900 text-center mb-4">
          Вход в приложение
        </h2>

        {/* Tab selector */}
        <div className="flex gap-1.5 p-1.5 bg-slate-100 rounded-xl mb-4">
          <button
            type="button"
            onClick={() => {
              setActiveTab("telegram");
              setEmailError(null);
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-all active:scale-[0.98] ${
              activeTab === "telegram"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <IconTelegram className="w-4 h-4" />
            <span>Telegram</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab("email");
              setWidgetError(null);
            }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-medium transition-all active:scale-[0.98] ${
              activeTab === "email"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>Email</span>
          </button>
        </div>

        {/* Error messages */}
        {(error || widgetError || emailError) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-700 text-xs xs:text-sm text-center font-medium">
              {emailError || widgetError || error}
            </p>
          </div>
        )}

        {/* Telegram Tab - always in DOM when browser, hidden via CSS so widget doesn't unmount */}
        <div
          className="flex flex-col items-center gap-4"
          style={{ display: activeTab === "telegram" ? "flex" : "none" }}
        >
          {/* Auth in progress overlay */}
          {isWidgetLoading && (
            <div className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="flex items-center justify-center gap-3">
                <div className="w-5 h-5 border-2 border-slate-200 border-t-[var(--color-accent)] rounded-full animate-spin" />
                <p className="text-slate-700 text-sm font-medium">Авторизация...</p>
              </div>
            </div>
          )}

          {/* User not in bot — show deeplink first */}
          {showWidgetNotInBot && (
            <div className="w-full space-y-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-900 font-medium text-center">
                Вы ещё не начинали бота
              </p>
              <p className="text-xs text-amber-700 text-center">
                Сначала откройте приложение в Telegram, затем войдите здесь через виджет
              </p>
              <a
                href={config.miniAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] no-underline"
                style={{ background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)" }}
              >
                <IconTelegram className="w-5 h-5" />
                <span>Открыть в Telegram</span>
              </a>
              <button
                type="button"
                onClick={() => setShowWidgetNotInBot(false)}
                className="w-full text-center text-xs text-amber-600 hover:text-amber-800"
              >
                Уже открыл — войти снова
              </button>
            </div>
          )}

          {/* Custom branded Telegram button (overlay over transparent widget) */}
          {!showWidgetNotInBot && (
            <div className="w-full min-h-[48px]">
              <TelegramLoginButton
                botName={config.botUsername}
                onAuth={handleTelegramAuth}
              />
            </div>
          )}

          {/* Fallback: deep link to open Mini App in Telegram */}
          <div className="w-full text-center relative z-10">
            <p className="text-xs text-slate-400 mb-2">или</p>
            <a
              href={config.miniAppUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm text-slate-700 bg-white border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all active:scale-[0.98] cursor-pointer touch-manipulation select-none no-underline"
            >
              <IconTelegram className="w-4 h-4 text-slate-500" />
              <span>Открыть в Telegram</span>
            </a>
          </div>
        </div>

        {/* Email Tab */}
        {activeTab === "email" && (
          <div>
            <datalist id="last-emails">
              {getLastEmails().map((e) => (
                <option key={e} value={e} />
              ))}
            </datalist>
            {emailStep === "password" && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email-pwd" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email-pwd"
                    list="last-emails"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 outline-none transition-all text-sm text-slate-900"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Пароль
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 bg-white focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 outline-none transition-all text-sm text-slate-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                      title={showPassword ? "Скрыть пароль" : "Показать пароль"}
                      aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberEmail}
                    onChange={(e) => setRememberEmail(e.target.checked)}
                    className="rounded border-slate-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)]/20"
                  />
                  <span>Запомнить email</span>
                </label>
                <button
                  type="submit"
                  disabled={isEmailLoading || !email || !password}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] disabled:opacity-50"
                  style={{
                    background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                  }}
                >
                  {isEmailLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Вход...</span>
                    </>
                  ) : (
                    <span>Войти</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleRequestMagicLink}
                  disabled={isEmailLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition-colors disabled:opacity-50"
                >
                  Войти по коду из письма
                </button>
              </form>
            )}

            {emailStep === "code" && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    Введите код из письма
                  </h3>
                  <p className="text-sm text-slate-500">
                    Отправлен на <strong className="text-slate-700">{email}</strong>
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Код действует 30 минут
                  </p>
                </div>

                <form onSubmit={handleCodeSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="login-code" className="sr-only">
                      Код из письма
                    </label>
                    <input
                      id="login-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      value={
                        code.length <= 3
                          ? code
                          : `${code.slice(0, 3)}-${code.slice(3, 6)}`
                      }
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setCode(val);
                        setEmailError(null);
                      }}
                      placeholder="123-456"
                      maxLength={7}
                      className="w-full px-4 py-4 rounded-xl border border-slate-200 bg-white focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 outline-none transition-all text-center text-2xl font-mono tracking-[0.3em] text-slate-900"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isEmailLoading || code.length !== 6}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] disabled:opacity-50"
                    style={{
                      background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                    }}
                  >
                    {isEmailLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Проверка...</span>
                      </>
                    ) : (
                      <span>Войти</span>
                    )}
                  </button>
                </form>

                <div className="flex flex-col items-stretch gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setEmailStep("password")}
                    className="py-2 text-sm font-medium text-[var(--color-accent)] hover:underline"
                  >
                    Войти с паролем
                  </button>
                  <button
                    type="button"
                    onClick={handleRequestMagicLink}
                    disabled={isEmailLoading}
                    className="py-2 text-sm font-medium text-[var(--color-accent)] hover:underline disabled:opacity-50"
                  >
                    Отправить код повторно
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailStep("password");
                      setEmail("");
                      setCode("");
                    }}
                    className="py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Использовать другой email
                  </button>
                </div>
              </div>
            )}

            {emailStep === "email_not_found" && (
              <div className="py-2 space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                  <p className="text-sm text-amber-900 font-medium mb-1">
                    Аккаунт с этой почтой не найден
                  </p>
                  <p className="text-xs text-amber-700">
                    Вход по почте доступен только тем, кто уже привязал её в приложении. Войдите через Telegram:
                  </p>
                </div>
                <a
                  href={config.miniAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all active:scale-[0.98] no-underline"
                  style={{ background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)" }}
                >
                  <IconTelegram className="w-5 h-5" />
                  <span>Открыть в Telegram</span>
                </a>
                <p className="text-xs text-slate-500 text-center">
                  Или войдите через виджет Telegram выше (вкладка «Telegram»)
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setEmailStep("password");
                    setEmailError(null);
                  }}
                  className="w-full text-center text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  ← Другой email
                </button>
              </div>
            )}
          </div>
        )}

        {/* Info about bot linking */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-[10px] xs:text-xs text-slate-500 text-center">
            После входа вы сможете подключить бота для получения уведомлений о
            событиях и билетах
          </p>
        </div>
      </div>

      {/* Footer */}
      <p className="mt-5 xs:mt-6 md-mobile:mt-8 text-slate-400 text-[10px] xs:text-xs text-center px-4 max-w-xs">
        Используя приложение, вы соглашаетесь с условиями использования
      </p>
    </div>
  );
}
